import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Webhook,
  Plus,
  RefreshCw,
  Edit,
  Trash2,
  PlayCircle,
  Check,
  X,
  Link,
  Shield,
  AlertCircle,
  Clock,
  ExternalLink,
  History,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

const eventTypeLabels = {
  'payment.success': 'Успешный платёж',
  'payment.failed': 'Отклонённый платёж',
  'payment.refunded': 'Возврат платежа',
  'merchant.created': 'Создан мерчант',
  'merchant.updated': 'Обновлён мерчант',
  'merchant.deleted': 'Удалён мерчант',
  'aml.alert': 'AML алерт',
  'aml.blocked': 'AML блокировка',
  'user.created': 'Создан пользователь',
  'user.updated': 'Обновлён пользователь',
};

const WebhooksPage = () => {
  const { hasPermission } = useAuth();
  const { t, language } = useLanguage();
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingWebhook, setEditingWebhook] = useState(null);
  const [deleteWebhook, setDeleteWebhook] = useState(null);
  const [testingId, setTestingId] = useState(null);
  const [showLogs, setShowLogs] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    url: '',
    secret: '',
    events: [],
    is_active: true,
  });
  const [saving, setSaving] = useState(false);

  const canWrite = hasPermission('settings', 'write');
  const canDelete = hasPermission('settings', 'delete');

  const fetchWebhooks = async () => {
    try {
      setLoading(true);
      const response = await api.get('/webhooks');
      setWebhooks(response.data.webhooks || []);
    } catch (err) {
      toast.error('Ошибка загрузки webhooks');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWebhooks();
  }, []);

  const openCreateDialog = () => {
    setEditingWebhook(null);
    setFormData({
      name: '',
      url: '',
      secret: '',
      events: [],
      is_active: true,
    });
    setShowDialog(true);
  };

  const openEditDialog = (webhook) => {
    setEditingWebhook(webhook);
    setFormData({
      name: webhook.name,
      url: webhook.url,
      secret: '',
      events: webhook.events || [],
      is_active: webhook.is_active,
    });
    setShowDialog(true);
  };

  const handleEventToggle = (event) => {
    setFormData(prev => ({
      ...prev,
      events: prev.events.includes(event)
        ? prev.events.filter(e => e !== event)
        : [...prev.events, event]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Введите название webhook');
      return;
    }
    if (!formData.url.trim()) {
      toast.error('Введите URL');
      return;
    }
    if (formData.events.length === 0) {
      toast.error('Выберите хотя бы одно событие');
      return;
    }
    
    try {
      setSaving(true);
      const data = { ...formData };
      if (!data.secret) delete data.secret;
      
      if (editingWebhook) {
        await api.put(`/webhooks/${editingWebhook.id}`, data);
        toast.success('Webhook обновлён');
      } else {
        await api.post('/webhooks', data);
        toast.success('Webhook создан');
      }
      
      setShowDialog(false);
      fetchWebhooks();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteWebhook) return;
    
    try {
      await api.delete(`/webhooks/${deleteWebhook.id}`);
      toast.success('Webhook удалён');
      setDeleteWebhook(null);
      fetchWebhooks();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка удаления');
    }
  };

  const handleTest = async (webhookId) => {
    try {
      setTestingId(webhookId);
      const response = await api.post(`/webhooks/${webhookId}/test`);
      if (response.data.success) {
        toast.success('Тест успешен!');
      } else {
        toast.error(`Ошибка: ${response.data.error_message || 'Не удалось отправить'}`);
      }
    } catch (err) {
      toast.error('Ошибка тестирования');
    } finally {
      setTestingId(null);
    }
  };

  const fetchLogs = async (webhookId) => {
    try {
      setLogsLoading(true);
      setShowLogs(webhookId);
      const response = await api.get(`/webhooks/${webhookId}/logs`);
      setLogs(response.data.logs || []);
    } catch (err) {
      toast.error('Ошибка загрузки логов');
    } finally {
      setLogsLoading(false);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="webhooks-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-white">Webhooks</h1>
          <p className="text-sm text-slate-400 mt-1">Настройка HTTP уведомлений</p>
        </div>
        <div className="flex items-center gap-2">
          {canWrite && (
            <Button
              onClick={openCreateDialog}
              className="bg-emerald-500 hover:bg-emerald-600 text-white"
              data-testid="add-webhook-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">{t('common.create')}</span>
            </Button>
          )}
          <Button
            onClick={fetchWebhooks}
            variant="outline"
            size="sm"
            className="border-slate-600 text-slate-300 hover:bg-slate-700"
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Info Card */}
      <Card className="bg-blue-500/10 border-blue-500/30">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Webhook className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm text-blue-300 font-medium">Что такое Webhooks?</p>
              <p className="text-sm text-blue-200/70 mt-1">
                Webhooks позволяют получать уведомления о событиях в реальном времени на ваш сервер. 
                При наступлении события, OxyPay отправит HTTP POST запрос на указанный URL.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Webhooks List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <RefreshCw className="w-8 h-8 text-emerald-500 animate-spin" />
        </div>
      ) : webhooks.length === 0 ? (
        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardContent className="py-16 text-center">
            <Webhook className="w-12 h-12 mx-auto mb-4 text-slate-500" />
            <p className="text-slate-400 mb-4">Webhooks не настроены</p>
            {canWrite && (
              <Button onClick={openCreateDialog} className="bg-emerald-500 hover:bg-emerald-600">
                <Plus className="w-4 h-4 mr-2" />
                Создать первый webhook
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {webhooks.map((webhook) => (
            <Card key={webhook.id} className="bg-slate-800/50 border-slate-700/50">
              <CardContent className="p-4 sm:p-6">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-lg font-semibold text-white truncate">{webhook.name}</h3>
                      {webhook.is_active ? (
                        <Badge variant="outline" className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                          <Check className="w-3 h-3 mr-1" />
                          {t('common.active')}
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-slate-500/20 text-slate-400 border-slate-500/30">
                          <X className="w-3 h-3 mr-1" />
                          Отключен
                        </Badge>
                      )}
                      {webhook.failure_count > 0 && (
                        <Badge variant="outline" className="bg-red-500/20 text-red-400 border-red-500/30">
                          <AlertCircle className="w-3 h-3 mr-1" />
                          {webhook.failure_count} ошибок
                        </Badge>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2 text-sm text-slate-400 mb-3">
                      <Link className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate font-mono text-xs">{webhook.url}</span>
                    </div>
                    
                    <div className="flex flex-wrap gap-1.5">
                      {webhook.events?.map((event) => (
                        <Badge
                          key={event}
                          variant="outline"
                          className="bg-slate-700/50 text-slate-300 border-slate-600 text-xs"
                        >
                          {eventTypeLabels[event] || event}
                        </Badge>
                      ))}
                    </div>
                    
                    {webhook.last_triggered && (
                      <div className="flex items-center gap-1 mt-2 text-xs text-slate-500">
                        <Clock className="w-3 h-3" />
                        Последний вызов: {new Date(webhook.last_triggered).toLocaleString('ru-RU')}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleTest(webhook.id)}
                      disabled={testingId === webhook.id}
                      className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/20"
                      data-testid={`test-webhook-${webhook.id}`}
                    >
                      {testingId === webhook.id ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <PlayCircle className="w-4 h-4" />
                      )}
                      <span className="ml-1 hidden sm:inline">Тест</span>
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fetchLogs(webhook.id)}
                      className="border-slate-600 text-slate-300 hover:bg-slate-700"
                    >
                      <History className="w-4 h-4" />
                    </Button>
                    {canWrite && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditDialog(webhook)}
                        className="border-slate-600 text-slate-300 hover:bg-slate-700"
                        data-testid={`edit-webhook-${webhook.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                    )}
                    {canDelete && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setDeleteWebhook(webhook)}
                        className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                        data-testid={`delete-webhook-${webhook.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editingWebhook ? <Edit className="w-5 h-5 text-emerald-500" /> : <Plus className="w-5 h-5 text-emerald-500" />}
              {editingWebhook ? 'Редактировать Webhook' : 'Новый Webhook'}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Настройте HTTP уведомления для событий
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Название *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Мой Webhook"
                className="bg-slate-700/50 border-slate-600 text-white"
                data-testid="webhook-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">URL *</Label>
              <Input
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                placeholder="https://example.com/webhook"
                className="bg-slate-700/50 border-slate-600 text-white font-mono text-sm"
                data-testid="webhook-url-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Secret (опционально)</Label>
              <Input
                type="password"
                value={formData.secret}
                onChange={(e) => setFormData({ ...formData, secret: e.target.value })}
                placeholder="Секретный ключ для подписи"
                className="bg-slate-700/50 border-slate-600 text-white"
              />
              <p className="text-xs text-slate-500">
                Используется для HMAC-SHA256 подписи в заголовке X-Webhook-Signature
              </p>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">События *</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 p-3 bg-slate-700/30 rounded-lg max-h-48 overflow-y-auto">
                {Object.entries(eventTypeLabels).map(([key, label]) => (
                  <label
                    key={key}
                    className="flex items-center gap-2 p-2 rounded hover:bg-slate-700/50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={formData.events.includes(key)}
                      onChange={() => handleEventToggle(key)}
                      className="rounded border-slate-600 bg-slate-700 text-emerald-500 focus:ring-emerald-500"
                    />
                    <span className="text-sm text-slate-300">{label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg">
              <div>
                <Label className="text-slate-300">{t('common.status')}</Label>
                <p className="text-xs text-slate-500 mt-1">
                  {formData.is_active ? 'Webhook активен' : 'Webhook отключен'}
                </p>
              </div>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
            </div>

            <DialogFooter className="mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowDialog(false)}
                className="border-slate-600 text-slate-300"
              >
                {t('common.cancel')}
              </Button>
              <Button
                type="submit"
                className="bg-emerald-500 hover:bg-emerald-600 text-white"
                disabled={saving}
                data-testid="save-webhook-btn"
              >
                {saving && <RefreshCw className="w-4 h-4 animate-spin mr-2" />}
                {editingWebhook ? 'Сохранить' : 'Создать'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteWebhook} onOpenChange={() => setDeleteWebhook(null)}>
        <AlertDialogContent className="bg-slate-800 border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Удалить webhook?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              Вы действительно хотите удалить webhook <strong className="text-white">{deleteWebhook?.name}</strong>?
              Это действие нельзя отменить.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-slate-600 text-slate-300 hover:bg-slate-700">
              {t('common.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Logs Dialog */}
      <Dialog open={!!showLogs} onOpenChange={() => setShowLogs(null)}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-blue-500" />
              История вызовов
            </DialogTitle>
          </DialogHeader>
          
          <div className="max-h-96 overflow-y-auto space-y-2">
            {logsLoading ? (
              <div className="flex items-center justify-center py-10">
                <RefreshCw className="w-6 h-6 text-emerald-500 animate-spin" />
              </div>
            ) : logs.length === 0 ? (
              <div className="text-center py-10 text-slate-400">
                Нет записей
              </div>
            ) : (
              logs.map((log) => (
                <div
                  key={log.id}
                  className={`p-3 rounded-lg border ${
                    log.success 
                      ? 'bg-emerald-500/10 border-emerald-500/30' 
                      : 'bg-red-500/10 border-red-500/30'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {log.success ? (
                        <Check className="w-4 h-4 text-emerald-400" />
                      ) : (
                        <X className="w-4 h-4 text-red-400" />
                      )}
                      <Badge variant="outline" className="text-xs">
                        {eventTypeLabels[log.event_type] || log.event_type}
                      </Badge>
                      {log.response_status && (
                        <span className="text-xs text-slate-400">
                          HTTP {log.response_status}
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-slate-500">
                      {log.duration_ms}ms
                    </span>
                  </div>
                  <p className="text-xs text-slate-400">
                    {new Date(log.created_at).toLocaleString('ru-RU')}
                  </p>
                  {log.error_message && (
                    <p className="text-xs text-red-400 mt-1">{log.error_message}</p>
                  )}
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WebhooksPage;
